// src/pages/ReportsPage.js
import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  PlusCircle, 
  Eye, 
  Download, 
  Edit, 
  Pen, 
  Send, 
  FileSpreadsheet, 
  AlertCircle, 
  ChevronDown,
  Filter,
  Clock,
  Search,
  ArrowUpDown,
  Users,
  DollarSign,
  Calendar,
  Trash2,
  ExternalLink
} from 'lucide-react';
import { reportsAPI, projectsAPI, invoicesAPI } from '../services/api';
import { openReportPdf } from '../utils/urlHelpers';
import SignatureModal from '../components/SignatureModal';
import ReportConfirmationModal from '../components/ReportConfirmationModal';
import { useNavigate } from 'react-router-dom';

const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false); // Neuer State für Löschbestätigung
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null); // Für das Löschen
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusDropdownId, setStatusDropdownId] = useState(null);
  const [formData, setFormData] = useState({
    project_id: '',
    title: '',
    content: ''
  });
  const navigate = useNavigate();

  // Status-Definitionen mit Farben und Icons
  const STATUS_CONFIG = {
    draft: { 
      label: 'Entwurf', 
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200', 
      icon: <Clock className="h-4 w-4 mr-1" /> 
    },
    signed: { 
      label: 'Unterschrieben', 
      color: 'bg-blue-100 text-blue-800 border-blue-200', 
      icon: <Pen className="h-4 w-4 mr-1" /> 
    },
    sent: { 
      label: 'Gesendet', 
      color: 'bg-green-100 text-green-800 border-green-200', 
      icon: <Send className="h-4 w-4 mr-1" /> 
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [reportsResponse, projectsResponse] = await Promise.all([
          reportsAPI.getAll(),
          projectsAPI.getAll()
        ]);
        
        setReports(reportsResponse.data.reports);
        setProjects(projectsResponse.data.projects);
        setError('');
      } catch (err) {
        console.error('Fehler beim Laden der Daten:', err);
        setError('Fehler beim Laden der Daten');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Toggle-Funktion für das Status-Dropdown
  const toggleStatusDropdown = (reportId, e) => {
    // Event stoppen, damit es nicht bis zum Dokument propagiert
    if (e) e.stopPropagation();
    
    // Status umschalten
    setStatusDropdownId(statusDropdownId === reportId ? null : reportId);
  };
  
  // Event-Listener für Klicks außerhalb des Dropdowns
  useEffect(() => {
    // Nur hinzufügen, wenn ein Dropdown geöffnet ist
    if (statusDropdownId === null) return;
    
    const handleClickOutside = (e) => {
      const dropdown = document.querySelector('[data-dropdown="' + statusDropdownId + '"]');
      if (dropdown && !dropdown.contains(e.target)) {
        setStatusDropdownId(null);
      }
    };
    
    // Auf Dokument-Ebene registrieren
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup bei Unmount oder Änderung
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [statusDropdownId]);

  // Funktion zum Ändern des Status eines Reports
  const handleStatusChange = async (reportId, newStatus) => {
    try {
      setLoading(true);
      
      // API-Aufruf zum Aktualisieren des Status
      await reportsAPI.updateStatus(reportId, newStatus);
      
      // Reportliste aktualisieren
      const updatedReportsResponse = await reportsAPI.getAll();
      setReports(updatedReportsResponse.data.reports);
      
      // Status-Dropdown schließen
      setStatusDropdownId(null);
      
      setLoading(false);
    } catch (err) {
      setError('Fehler beim Aktualisieren des Status');
      console.error(err);
      setLoading(false);
    }
  };

  // Neue Funktion zum Löschen eines Rapports
  const handleDeleteReport = async () => {
    if (!selectedReport) return;
    
    try {
      setLoading(true);
      
      // API-Aufruf zum Löschen des Rapports
      await reportsAPI.delete(selectedReport.id);
      
      // Reportliste aktualisieren
      const updatedReportsResponse = await reportsAPI.getAll();
      setReports(updatedReportsResponse.data.reports);
      
      // Modal schließen
      setDeleteConfirmModalOpen(false);
      setSelectedReport(null);
      
      setLoading(false);
    } catch (err) {
      setError('Fehler beim Löschen des Rapports');
      console.error(err);
      setLoading(false);
    }
  };

  // Filterfunktion für Rapporte
  const filteredReports = reports
    .filter(report => 
      !selectedStatus || report.status === selectedStatus
    )
    .filter(report => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        report.title.toLowerCase().includes(searchLower) ||
        (report.project_title && report.project_title.toLowerCase().includes(searchLower)) ||
        (report.client_name && report.client_name.toLowerCase().includes(searchLower))
      );
    });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.project_id) {
      setError('Bitte wählen Sie einen Auftrag aus.');
      return;
    }
    
    setSelectedProjectId(formData.project_id);
    setConfirmModalOpen(true);
  };

  const createReportAfterConfirm = async () => {
    try {
      let title = formData.title;
      if (formData.project_id) {
        const selectedProject = projects.find(p => p.id.toString() === formData.project_id);
        if (selectedProject) {
          title = `Rapport: ${selectedProject.title} (${new Date().toLocaleDateString('de-DE')})`;
        }
      }
      
      const createResponse = await reportsAPI.create({
        ...formData,
        title: title
      });
      
      const getResponse = await reportsAPI.getAll();
      setReports(getResponse.data.reports);
      
      setShowForm(false);
      setConfirmModalOpen(false);
      setFormData({
        project_id: '',
        title: '',
        content: ''
      });
      setError('');
    } catch (err) {
      console.error('Fehler beim Erstellen des Rapports:', err);
      setError(`Fehler: ${err.response?.data?.error || err.message || 'Unbekannter Fehler'}`);
    }
  };

  // Rechnung aus dem Rapport erstellen
  const createInvoiceFromReport = async (reportId) => {
    try {
      // Statt API-Aufruf einfach zur Rechnungserstellungsseite weiterleiten
      window.location.href = `/rechnungen/erstellen/${reportId}`;
    } catch (err) {
      setError('Fehler bei der Weiterleitung zur Rechnungserstellung');
      console.error(err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0,00';
    return parseFloat(amount).toFixed(2).replace('.', ',');
  };

  if (loading && !reports.length) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-primary-200 mb-3 sm:mb-4"></div>
          <div className="h-3 sm:h-4 w-24 sm:w-32 bg-primary-100 rounded"></div>
          <div className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-500">Lade Rapportdaten...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
      {/* Header mit Titel und Aktionen */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-8 gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Rapportverwaltung</h1>
          <p className="text-sm sm:text-base text-gray-500 mt-1">Erstellen und verwalten Sie Ihre Auftrags-Rapporte</p>
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            if (!showForm) {
              setFormData({
                project_id: '',
                title: '',
                content: ''
              });
            }
          }}
          className={`flex items-center w-full sm:w-auto justify-center ${
            showForm 
              ? 'bg-gray-200 hover:bg-gray-300 text-gray-800' 
              : 'bg-primary-600 hover:bg-primary-700 text-white'
          } px-4 py-2 rounded-lg transition duration-150 shadow-sm`}
        >
          {showForm ? (
            <>
              <Edit size={18} className="mr-2" /> Abbrechen
            </>
          ) : (
            <>
              <PlusCircle size={18} className="mr-2" /> Neuer Rapport
            </>
          )}
        </button>
      </div>

      {/* Fehlermeldung */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4 mb-4 sm:mb-6 rounded-r-lg shadow-sm">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-xs sm:text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Formular für neuen Rapport */}
      {showForm && (
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg mb-4 sm:mb-8 overflow-hidden border border-gray-200">
          <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
              Neuer Rapport erstellen
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="p-4 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Auftrag auswählen *</label>
                <select
                  name="project_id"
                  value={formData.project_id}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-400"
                >
                  <option value="">-- Auftrag auswählen --</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.title} ({project.company_name || `${project.first_name} ${project.last_name}`})
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Bemerkungen (optional)</label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-400"
                  rows="4"
                  placeholder="Zusätzliche Informationen zum Rapport..."
                />
              </div>
            </div>

            <div className="flex justify-end pt-3 sm:pt-4 border-t border-gray-200">
              <button
                type="submit"
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 border border-transparent text-sm sm:text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <FileText size={18} className="mr-2" /> Rapport generieren
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filter- und Suchleiste */}
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={16} className="text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rapporte durchsuchen..."
            className="block w-full pl-9 pr-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-400 shadow-sm text-sm"
          />
        </div>
        
        <div className="relative w-full sm:w-auto">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Filter size={16} className="text-gray-400" />
          </div>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="block w-full sm:w-auto pl-9 pr-8 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-400 shadow-sm appearance-none text-sm"
          >
            <option value="">Alle Status</option>
            <option value="draft">Entwurf</option>
            <option value="signed">Unterschrieben</option>
            <option value="sent">Gesendet</option>
          </select>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <ArrowUpDown size={14} className="text-gray-400" />
          </div>
        </div>
      </div>

      {/* Rapportliste */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
        <div className="p-3 sm:p-4 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row sm:justify-between sm:items-center">
          <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-0">Rapportliste</h2>
          <div className="text-xs sm:text-sm text-gray-500">
            {filteredReports.length} {filteredReports.length === 1 ? 'Rapport' : 'Rapporte'} gefunden
          </div>
        </div>
        
        {filteredReports.length === 0 ? (
          <div className="p-6 sm:p-8 text-center">
            <p className="text-sm sm:text-base text-gray-500">Keine Rapporte vorhanden</p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titel</th>
                  <th scope="col" className="hidden sm:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auftrag</th>
                  <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Betrag</th>
                  <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="hidden md:table-cell px-3 sm:px-6 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Erstellt</th>
                  <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReports.map((report) => {
                  const statusConfig = STATUS_CONFIG[report.status] || {};
                  return (
                    <tr key={report.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-normal">
                        <div className="text-xs sm:text-sm font-medium text-gray-900 line-clamp-2">
                          {report.title}
                        </div>
                        <div className="sm:hidden text-xs text-gray-600 mt-1">
                          {report.project_title}
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Users size={14} className="text-gray-400 mr-1 sm:mr-2" />
                          <div className="text-xs sm:text-sm text-gray-900">{report.project_title}</div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-right font-medium">
                        CHF {formatCurrency(report.total_amount)}
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-center">
                        <div className="relative" data-dropdown={report.id}>
                          <button 
                            onClick={(e) => toggleStatusDropdown(report.id, e)}
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color} border cursor-pointer`}
                          >
                            {statusConfig.icon}
                            <span className="hidden sm:inline">{statusConfig.label}</span>
                          </button>
                          
                          {/* Dropdown für Statusänderung */}
                          {statusDropdownId === report.id && (
                            <div className="absolute z-10 left-0 mt-1 sm:mt-2 w-40 sm:w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                              <div className="py-1" role="menu" aria-orientation="vertical">
                                <button
                                  onClick={() => handleStatusChange(report.id, 'draft')}
                                  className={`w-full text-left px-3 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm ${report.status === 'draft' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-100'}`}
                                  role="menuitem"
                                >
                                  <Clock size={14} className="inline mr-1 sm:mr-2 text-yellow-600" />
                                  Entwurf
                                </button>
                                <button
                                  onClick={() => handleStatusChange(report.id, 'signed')}
                                  className={`w-full text-left px-3 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm ${report.status === 'signed' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-100'}`}
                                  role="menuitem"
                                >
                                  <Pen size={14} className="inline mr-1 sm:mr-2 text-blue-600" />
                                  Unterschrieben
                                </button>
                                <button
                                  onClick={() => handleStatusChange(report.id, 'sent')}
                                  className={`w-full text-left px-3 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm ${report.status === 'sent' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-100'}`}
                                  role="menuitem"
                                >
                                  <Send size={14} className="inline mr-1 sm:mr-2 text-green-600" />
                                  Gesendet
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 text-right">
                        <div className="flex items-center justify-end">
                          <Calendar size={14} className="text-gray-400 mr-1 sm:mr-2" />
                          {formatDate(report.created_at)}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-right text-xs sm:text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2 sm:space-x-3">
                          {/* PDF-Vorschau anzeigen */}
                          <button 
                            onClick={() => openReportPdf(report.id)} 
                            className="text-primary-600 hover:text-primary-800 transition-colors"
                            title="PDF-Vorschau anzeigen"
                          >
                            <Eye size={20} className="sm:size-4" /> {/* Größer auf Mobilgeräten */}
                          </button>
                          
                          {/* Zur Detailansicht */}
                          <button 
                            onClick={() => navigate(`/rapporte/${report.id}`)} 
                            className="text-gray-600 hover:text-gray-800 transition-colors"
                            title="Rapport bearbeiten"
                          >
                            <Edit size={20} className="sm:size-4" />
                          </button>
                          
                          {/* PDF herunterladen */}
                          <button 
                            onClick={() => openReportPdf(report.id, true)} 
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title="PDF herunterladen"
                          >
                            <Download size={20} className="sm:size-4" /> {/* Größer auf Mobilgeräten */}
                          </button>
                          
                          {report.status === 'draft' && (
                            <button 
                              onClick={() => {
                                setSelectedReportId(report.id);
                                setSignatureModalOpen(true);
                              }}
                              className="text-amber-600 hover:text-amber-800 transition-colors"
                              title="Unterschreiben"
                            >
                              <Pen size={20} className="sm:size-4" /> {/* Größer auf Mobilgeräten */}
                            </button>
                          )}
                          
                          {/* Rechnung erstellen - für alle Rapporte verfügbar */}
                          <button 
                            onClick={() => createInvoiceFromReport(report.id)} 
                            className="text-green-600 hover:text-green-800 transition-colors"
                            title="Rechnung erstellen"
                          >
                            <DollarSign size={20} className="sm:size-4" /> {/* Größer auf Mobilgeräten */}
                          </button>
                          
                          {/* Löschen-Button - für alle Rapporte */}
                          <button 
                            onClick={() => {
                              setSelectedReport(report);
                              setDeleteConfirmModalOpen(true);
                            }}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title="Rapport löschen"
                          >
                            <Trash2 size={20} className="sm:size-4" /> {/* Größer auf Mobilgeräten */}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Signatur-Modal */}
      <SignatureModal 
        isOpen={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        onSave={async (reportId, signatureData) => {
          try {
            await reportsAPI.signReport(reportId, { signature_data: signatureData });
            const getResponse = await reportsAPI.getAll();
            setReports(getResponse.data.reports);
            setSignatureModalOpen(false);
            setError('');
          } catch (err) {
            console.error('Fehler beim Unterschreiben:', err);
            setError('Fehler beim Unterschreiben des Rapports');
          }
        }}
        reportId={selectedReportId}
      />

      {/* Bestätigungs-Modal für Einträge vor der Rapport-Erstellung */}
      <ReportConfirmationModal
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={createReportAfterConfirm}
        projectId={selectedProjectId}
      />
      
      {/* Bestätigungs-Modal für das Löschen eines Rapports */}
      {deleteConfirmModalOpen && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4">Rapport löschen</h3>
            <p className="mb-6">
              Möchten Sie den Rapport "{selectedReport.title}" wirklich löschen?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setDeleteConfirmModalOpen(false);
                  setSelectedReport(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDeleteReport}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
